/**
 * Centralized design tokens — single source of truth for all colors used in the app.
 * Import from here instead of hardcoding color values in components.
 *
 * Color values are sourced from the generated design-token artifact
 * (../theme.gen.ts <- packages/design-tokens/tokens.mjs). Edit values THERE.
 * This file only re-shapes them into the legacy `colors`/`semantic` API.
 */
import { palette, statusColors } from '../theme.gen';

export const colors = {
    primary: {
        main: palette.primary.main,
        dark: palette.primary.dark,
        darker: '#007173',
        light: palette.primary.light,
        bg: 'rgba(0, 167, 168, 0.08)',
        border: 'rgba(0, 167, 168, 0.2)',
    },

    secondary: {
        main: palette.secondary.main,
        dark: palette.secondary.dark,
        light: palette.secondary.light,
        bg: 'rgba(42, 73, 111, 0.08)',
        border: 'rgba(42, 73, 111, 0.2)',
    },

    slate: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
    },

    // Semantic "Sobre" palette (success/warning/danger/info), accordée au teal/navy.
    red: {
        main: statusColors.error,
        light: '#d2726f',
        bg: 'rgba(192, 80, 78, 0.06)',
        border: 'rgba(192, 80, 78, 0.2)',
    },

    green: {
        main: statusColors.success,
        dark: '#246b51',
        bg: 'rgba(47, 140, 107, 0.08)',
        border: 'rgba(47, 140, 107, 0.25)',
    },

    blue: {
        main: statusColors.info,
        dark: '#355a7e',
        bg: 'rgba(67, 113, 158, 0.08)',
        border: 'rgba(67, 113, 158, 0.25)',
    },

    purple: {
        main: '#8b5cf6',
        dark: '#7c3aed',
        bg: 'rgba(139, 92, 246, 0.08)',
        border: 'rgba(139, 92, 246, 0.25)',
    },

    orange: {
        main: statusColors.warning,
        dark: '#946a26',
        bg: 'rgba(188, 135, 48, 0.08)',
        border: 'rgba(188, 135, 48, 0.25)',
    },
} as const;

/** Commonly used semantic aliases */
export const semantic = {
    text: {
        primary: colors.slate[900],
        secondary: colors.slate[600],
        tertiary: colors.slate[500],
        disabled: colors.slate[400],
        inverse: '#FFFFFF',
    },
    border: {
        default: colors.slate[200],
        subtle: colors.slate[100],
        hover: colors.slate[300],
    },
    bg: {
        surface: '#FFFFFF',
        muted: colors.slate[50],
        subtle: colors.slate[100],
    },
} as const;
